import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { Boxes, Plus, Upload, Search, FileText } from 'lucide-react'
import { getSession } from '../lib/session'
import { pollJob } from '../lib/jobs'
import ProductGuide from '../components/ProductGuide'
import AgentAsk from '../components/AgentAsk'

export default function RagManager() {
  const session = getSession()
  const [collections, setCollections] = useState([])
  const [selected, setSelected] = useState(null)
  const [activeTab, setActiveTab] = useState('collections')
  const [showForm, setShowForm] = useState(false)
  const DEFAULT_FORM = {
    name: '', description: '',
    chunkStrategy: 'recursive', chunkSize: 550, chunkOverlap: 110,
    embeddingModel: 'openai/text-embedding-3-small', distanceMetric: 'cosine'
  }
  const [form, setForm] = useState(DEFAULT_FORM)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [docContent, setDocContent] = useState('')
  const [query, setQuery] = useState('')
  const [answer, setAnswer] = useState(null)

  const loadCollections = () => {
    if (!session?.namespaceId) return
    axios.get('/api/v1/rag/collections')
      .then(res => setCollections(res.data.collections || []))
      .catch(err => toast.error(err.response?.data?.error || 'Failed to load collections'))
  }
  useEffect(loadCollections, [])

  const createCollection = async (e) => {
    e.preventDefault()
    try {
      await axios.post('/api/v1/rag/collections', {
        name: form.name,
        description: form.description,
        config: {
          chunkStrategy: form.chunkStrategy,
          chunkSize: Number(form.chunkSize),
          chunkOverlap: Number(form.chunkOverlap),
          embeddingModel: form.embeddingModel,
          distanceMetric: form.distanceMetric
        }
      })
      toast.success('Collection created')
      setForm(DEFAULT_FORM)
      setShowAdvanced(false)
      setShowForm(false)
      loadCollections()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create collection')
    }
  }

  const addDocument = async () => {
    if (!docContent.trim() || !selected) return
    toast.loading('Queuing ingestion...')
    try {
      const res = await axios.post(`/api/v1/rag/collections/${selected.id}/documents`, { content: docContent })
      const done = res.data.jobId ? await pollJob(res.data.jobId) : null
      toast.dismiss()
      toast.success(done ? `Ingested ${done.chunks ?? ''} chunk(s)` : 'Document ingested')
      setDocContent('')
      loadCollections()
    } catch (err) {
      toast.dismiss()
      toast.error(err.response?.data?.error || 'Ingest failed')
    }
  }

  const runQuery = async () => {
    if (!query.trim() || !selected) return
    setAnswer({ loading: true })
    try {
      const res = await axios.post(`/api/v1/rag/collections/${selected.id}/query`, { query })
      setAnswer(res.data)
    } catch (err) {
      setAnswer(null)
      toast.error(err.response?.data?.error || 'Query failed')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Custom RAG</h2>
          <p className="text-gray-500 mt-1">Isolated, per-tenant retrieval collections with a query API</p>
        </div>
        <button onClick={() => setShowForm(true)} disabled={!session?.namespaceId}
          className="btn-primary flex items-center gap-2 disabled:opacity-50">
          <Plus size={18} /> New Collection
        </button>
      </div>

      <ProductGuide product="rag" collapsible defaultOpen={false} />

      <div className="max-w-2xl"><AgentAsk service="rag" agentName="RAG Query Engine" starters={['How many collections do I have?', 'What can I query right now?']} /></div>

      {!session?.namespaceId && (
        <div className="card bg-amber-500/10 border-amber-500/30 text-sm text-amber-600">
          No tenant connected. Go to <Link to="/setup" className="font-semibold underline">Setup</Link> first.
        </div>
      )}

      {showForm && (
        <div className="card max-w-xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">New Collection</h3>
          <form onSubmit={createCollection} className="space-y-3">
            <input type="text" required className="input" placeholder="Collection name (e.g. Product FAQ)"
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <input type="text" className="input" placeholder="Description (optional)"
              value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />

            <button type="button" onClick={() => setShowAdvanced(v => !v)}
              className="text-sm text-primary-600 font-medium">
              {showAdvanced ? '▾ Hide' : '▸ Advanced'} — chunking, embeddings & distance
            </button>

            {showAdvanced && (
              <div className="space-y-4 rounded-lg border border-gray-200 p-4 bg-gray-50/60">
                <p className="text-xs text-gray-500">Defaults are tuned to keep distinct facts (like separate prices) in separate chunks. Change these only if you know your data — everything works out of the box.</p>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Chunking strategy</label>
                  <select className="input" value={form.chunkStrategy} onChange={e => setForm({ ...form, chunkStrategy: e.target.value })}>
                    <option value="recursive">Recursive — split on headings/paragraphs, then sentences (recommended)</option>
                    <option value="semantic">Semantic — split where the topic shifts</option>
                    <option value="fixed">Fixed-size — cut every N characters with overlap</option>
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">How documents are broken into searchable pieces before embedding.</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Chunk size (chars)</label>
                    <input type="number" min="100" max="4000" className="input" value={form.chunkSize}
                      onChange={e => setForm({ ...form, chunkSize: e.target.value })} />
                    <p className="text-[11px] text-gray-400 mt-1">Smaller = more precise; larger = more context.</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Overlap (chars)</label>
                    <input type="number" min="0" max="1000" className="input" value={form.chunkOverlap}
                      onChange={e => setForm({ ...form, chunkOverlap: e.target.value })} />
                    <p className="text-[11px] text-gray-400 mt-1">Carried between chunks so context isn't cut mid-thought.</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Embedding model</label>
                  <select className="input" value={form.embeddingModel} onChange={e => setForm({ ...form, embeddingModel: e.target.value })}>
                    <option value="openai/text-embedding-3-small">text-embedding-3-small (1536-dim, fast &amp; cheap)</option>
                    <option value="openai/text-embedding-ada-002">text-embedding-ada-002 (1536-dim, legacy)</option>
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">Turns text into vectors. Both are 1536-dim to fit the index.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Distance metric</label>
                  <select className="input" value={form.distanceMetric} onChange={e => setForm({ ...form, distanceMetric: e.target.value })}>
                    <option value="cosine">Cosine — direction of meaning (recommended, index-accelerated)</option>
                    <option value="l2">Euclidean (L2) — straight-line distance</option>
                    <option value="dot">Dot product — alignment + magnitude</option>
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">How “closeness” is measured at query time. Index (HNSW/IVFFlat) is a platform-level setting — cosine is the accelerated path today.</p>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button type="submit" className="btn-primary">Create</button>
              <button type="button" onClick={() => { setShowForm(false); setShowAdvanced(false) }} className="btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {collections.map(col => (
          <div key={col.id}
            onClick={() => { setSelected(col); setActiveTab('manage'); setAnswer(null) }}
            className={`card cursor-pointer transition-all ${selected?.id === col.id ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}>
            <div className="flex items-center justify-between">
              <Boxes className="text-primary-500" size={26} />
              <span className="text-xs text-gray-400">{col.chunk_count ?? 0} chunks</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mt-3">{col.name}</h3>
            {col.description && <p className="text-xs text-gray-500 mt-1">{col.description}</p>}
            <p className="text-xs mt-2 font-medium text-primary-600">{(col.chunk_count ?? 0) === 0 ? 'Empty — click to add documents' : 'Click to add docs & query →'}</p>
          </div>
        ))}
        {collections.length === 0 && session?.namespaceId && (
          <p className="text-sm text-gray-400">No collections yet. Create one to get started.</p>
        )}
      </div>

      {selected && activeTab === 'manage' && (
        <div className="card max-w-2xl space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Boxes size={20} className="text-primary-500" /> {selected.name}
            </h3>
            <p className="text-xs text-gray-500 mt-1">Collection ID: {selected.id}</p>
            {selected.config && Object.keys(selected.config).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[
                  ['chunk', `${selected.config.chunkStrategy || 'recursive'} · ${selected.config.chunkSize || 550}/${selected.config.chunkOverlap ?? 110}`],
                  ['embed', selected.config.embeddingModel || selected.embedding_model || 'default'],
                  ['distance', selected.config.distanceMetric || 'cosine']
                ].map(([k, v]) => (
                  <span key={k} className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600">
                    <span className="text-gray-400">{k}:</span> {v}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
              <Upload size={16} /> Add document
            </label>
            <textarea className="input h-28" placeholder="Paste text to add to this collection..."
              value={docContent} onChange={e => setDocContent(e.target.value)} />
            <button onClick={addDocument} className="btn-secondary text-sm mt-2 flex items-center gap-1">
              <FileText size={14} /> Ingest
            </button>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
              <Search size={16} /> Query this collection
            </label>
            <div className="flex gap-2">
              <input type="text" className="input flex-1" placeholder="Ask a question..."
                value={query} onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runQuery()} />
              <button onClick={runQuery} className="btn-primary px-6">Ask</button>
            </div>
            {answer && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm">
                {answer.loading ? <span className="text-gray-400 animate-pulse">Retrieving…</span> : (
                  <>
                    <p className="text-gray-800">{answer.answer}</p>
                    <p className="text-xs text-gray-400 mt-2">{answer.sources?.length || 0} source chunk(s)</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
